import { useState } from "react";
import { Building2, FileText, Boxes, Loader2, Package, Heart } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { useToast } from "@/shared/hooks/use-toast";
import { exportSurvey, exportAssets } from "@/features/exports/services";

interface PortfolioSummary {
  totalProperties: number;
  totalAssets: number;
}

/** The two in-page views: chat is the landing surface, "General dashboard" is
 *  the standing fixed-panel tab. URL stays /dashboard for both. */
export type DashboardView = "chat" | "general";

// PropertyDetailTabsNav's active-trigger styling — the repo's tab convention.
const VIEW_TAB_TRIGGER_CLASSES =
  "whitespace-nowrap data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-sm";

interface ClientDashboardChromeProps {
  clientId: string | undefined;
  clientName: string;
  clientLogoUrl?: string | null;
  portfolio: PortfolioSummary | null | undefined;
  exportsEnabled: boolean;
  /** Number of saved components — shown as a badge on the My Dashboard pill. */
  savedCount?: number;
  /** Opens the My Dashboard sheet. When omitted, the pill is hidden. */
  onOpenSaved?: () => void;
  /** Active in-page view. When omitted (with onViewChange), the tabs are hidden. */
  view?: DashboardView;
  onViewChange?: (view: DashboardView) => void;
}

export function ClientDashboardChrome({
  clientId,
  clientName,
  clientLogoUrl,
  portfolio,
  exportsEnabled,
  savedCount,
  onOpenSaved,
  view,
  onViewChange,
}: ClientDashboardChromeProps) {
  const { toast } = useToast();
  const [exporting, setExporting] = useState<"surveys" | "assets" | null>(null);

  return (
    <div className="relative bg-gradient-to-r from-card via-card/98 to-accent/20 backdrop-blur-xl border-b border-border/50 flex-shrink-0 overflow-hidden">
      {/* Subtle decorative gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-accent/5 pointer-events-none" />

      <div className="h-[72px] px-6 flex items-center relative z-10">
        <div className="flex items-center gap-5">
          {/* Logo */}
          <div className="relative group">
            {clientLogoUrl ? (
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <img
                  src={clientLogoUrl}
                  alt={`${clientName} logo`}
                  className="relative w-12 h-12 rounded-xl object-contain border border-border/50 shadow-sm transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            ) : (
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-primary via-primary/90 to-primary/70 flex items-center justify-center border border-primary/20 shadow-sm transition-transform duration-300 group-hover:scale-105">
                  <Building2 className="h-6 w-6 text-primary-foreground" />
                </div>
              </div>
            )}
          </div>

          {/* Title */}
          <div className="flex flex-col gap-1.5">
            <h1 className="text-xl font-bold text-foreground tracking-tight bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text">
              {clientName}
            </h1>
          </div>
        </div>

        {/* Right-side cluster: view tabs + My Dashboard + exports + portfolio summary cards */}
        <div className="ml-auto flex items-center gap-3">
        {view && onViewChange ? (
          <Tabs value={view} onValueChange={(v) => onViewChange(v as DashboardView)}>
            <TabsList className="bg-background/50 backdrop-blur-sm">
              <TabsTrigger value="chat" className={VIEW_TAB_TRIGGER_CLASSES}>
                Chat
              </TabsTrigger>
              <TabsTrigger value="general" className={VIEW_TAB_TRIGGER_CLASSES}>
                General dashboard
              </TabsTrigger>
            </TabsList>
          </Tabs>
        ) : null}
        {onOpenSaved ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenSaved}
            className="gap-2"
            aria-label="Open My Dashboard"
          >
            <Heart className="h-4 w-4" />
            My Dashboard
            {savedCount ? (
              <span className="ml-0.5 rounded-full bg-primary/15 px-1.5 text-xs font-semibold tabular-nums text-primary">
                {savedCount}
              </span>
            ) : null}
          </Button>
        ) : null}
        {exportsEnabled && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              if (!clientId) return;
              setExporting("surveys");
              try {
                await exportSurvey({
                  projectId: clientId,
                  projectName: clientName || "Project",
                  format: "xlsx",
                });
              } catch (err) {
                toast({
                  title: "Export failed",
                  description: err instanceof Error ? err.message : String(err),
                  variant: "destructive",
                });
              } finally {
                setExporting(null);
              }
            }}
            disabled={!clientId || exporting !== null}
            className="gap-2"
          >
            {exporting === "surveys" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            Export Surveys
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              if (!clientId) return;
              setExporting("assets");
              try {
                await exportAssets({
                  projectId: clientId,
                  label: clientName || "Project",
                  format: "xlsx",
                });
              } catch (err) {
                toast({
                  title: "Export failed",
                  description: err instanceof Error ? err.message : String(err),
                  variant: "destructive",
                });
              } finally {
                setExporting(null);
              }
            }}
            disabled={!clientId || exporting !== null}
            className="gap-2"
          >
            {exporting === "assets" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Boxes className="h-4 w-4" />
            )}
            Export Assets
          </Button>
        </div>
        )}
          {portfolio && (
            <div className="flex items-center gap-2">
              <div className="flex flex-col items-start gap-0.5 rounded-md border border-border/50 bg-background/80 px-3 py-1.5 min-w-[88px]">
                <div className="flex items-center gap-1">
                  <Building2 className="h-3 w-3 text-muted-foreground/70 flex-shrink-0" />
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">Sites</span>
                </div>
                <span className="text-sm font-semibold text-foreground tabular-nums">
                  {portfolio.totalProperties.toLocaleString()}
                </span>
              </div>
              <div className="flex flex-col items-start gap-0.5 rounded-md border border-border/50 bg-background/80 px-3 py-1.5 min-w-[88px]">
                <div className="flex items-center gap-1">
                  <Package className="h-3 w-3 text-muted-foreground/70 flex-shrink-0" />
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">Assets</span>
                </div>
                <span className="text-sm font-semibold text-foreground tabular-nums">
                  {portfolio.totalAssets.toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
